import { useAppStorage, useObservable } from '@aesop-fables/scrinium';
import { Typography, Card, CardContent, Button, Grid } from '@mui/material';
import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router';
import { Svgs } from '../../assets/svg';
import { Spacer, Row, Column } from '../../components';
import { GroupFeatureMapCompartments, groupFeatureMapStorageKey } from '../../data/groupFeatureMap';
import { LicenseCompartments, licenseStorageKey } from '../../data/license';
import { ProductTypeCompartments, productTypeStorageKey } from '../../data/productType';
import { getActivePrice, hasAvailablePackages } from '../../helpers/pricebookUtils';
import { filterByPayer, formatDollars } from '../../helpers/utilityFunctions';
import { GroupFeatureMapData } from '../../models/GroupFeatureMapData';
import { PricebookBundle, PaymentTypeEnum } from '../../models/PricebookData';
import PurchaseLicensesDrawer from '../license/PurchaseLicensesDrawer';
import MemberPaysMetricsCard from './MemberPaysMetricsCard';
import OrgPaysMetricsCard from './OrgPaysMetricsCard';
import { LicenseAgreementData } from '../../api/apis/LicenseAgreementApi';
import { OrganizationMetricsData } from '../../api/apis/OrgPricebookLicenseMetricsApi';
import { OrganizationCompartments, organizationStorageKey } from '../../data/organization';
import { OrganizationData } from '../../models/OrganizationData';
import { useBasePricebookOptions, usePricebookSelections } from '../../hooks/usePricebookOptions';
import { PaidByEnum } from '../../models/LicenseData';

const AtAGlance: React.FC = () => {
  const navigate = useNavigate();
  const appStorage = useAppStorage();

  const [packagesAvailable, setPackagesAvailable] = useState(false);
  const [purchaseDrawerOpen, setPurchaseDrawerOpen] = useState(false);
  const [selectedPricebook, setSelectedPricebook] = useState<PricebookBundle | undefined>(
    undefined,
  );

  const productTypeDataCache = appStorage.retrieve<ProductTypeCompartments>(productTypeStorageKey);
  const licenseDataCache = appStorage.retrieve<LicenseCompartments>(licenseStorageKey);
  const organizationDataCache =
    appStorage.retrieve<OrganizationCompartments>(organizationStorageKey);
  const groupFeatureMapDataCache =
    appStorage.retrieve<GroupFeatureMapCompartments>(groupFeatureMapStorageKey);

  const paidBy = useObservable(productTypeDataCache.observe$<string>('paidBy'));
  const licenseAgreements = useObservable(
    licenseDataCache.observe$<LicenseAgreementData[]>('licenseAgreement'),
  );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  const pricebookOptions = useBasePricebookOptions() ?? [];
  const pricebookSelections = usePricebookSelections();
  const groupFeatureMapData = useObservable(
    groupFeatureMapDataCache.observe$<GroupFeatureMapData>('groupFeatureMap'),
  );
  const orgMetrics = useObservable(
    organizationDataCache.observe$<OrganizationMetricsData[]>('allMetrics'),
  );
  const selectedOrg = useObservable(
    organizationDataCache.observe$<OrganizationData>('organization'),
  );

  const pendingAgreement = licenseAgreements?.find(
    licenseAgreement => licenseAgreement.docStatus && !licenseAgreement.hasAccepted,
  );
  const meteredPayment = pricebookSelections?.some(
    pricebook => pricebook.pricebook.paymentType === PaymentTypeEnum.Metered,
  );

  const selectedOrgMetrics = useMemo(() => {
    if (selectedOrg?.id && orgMetrics) {
      return orgMetrics.find(metric => metric.organizationId === selectedOrg.id);
    }
  }, [selectedOrg, orgMetrics]);

  const totalActiveUsers = useMemo(() => {
    const metrics = selectedOrgMetrics?.pricebookLicenseMetricsList || [];
    return metrics.reduce((sum, metric) => sum + metric.activeLicenses, 0);
  }, [selectedOrgMetrics]);

  useEffect(() => {
    const filteredPricebook = filterByPayer(pricebookOptions, paidBy);
    setPackagesAvailable(hasAvailablePackages(filteredPricebook, pricebookSelections));
  }, [paidBy, pricebookOptions, pricebookSelections]);

  const findPricebookById = (id: number): PricebookBundle | undefined => {
    const bundle = pricebookSelections?.find(pricebook => pricebook.pricebook.id === id);
    if (
      bundle &&
      bundle.pricebook &&
      (pricebookSelections?.filter(
        pricebook => pricebook.pricebook.pricebookName === bundle.pricebook.pricebookName,
      )?.length ?? 0) > 1
    ) {
      const bundleCopy = {
        pricebook: { ...bundle?.pricebook },
        prices: [...(bundle?.prices ?? [])],
      };
      const activeSubscription = getActivePrice(bundleCopy.prices);
      const price = (activeSubscription && activeSubscription.price / 100.0) ?? 0;

      bundleCopy.pricebook.pricebookName =
        bundleCopy.pricebook.pricebookName + ' (' + formatDollars(price) + ')';
      return bundleCopy;
    }
    return bundle;
  };

  const onPurchaseLicenses = (id: number) => {
    const pricebook = findPricebookById(id);
    setSelectedPricebook(pricebook);
    setPurchaseDrawerOpen(true);
  };

  const onAddPackage = () => {
    navigate('/addPackage', { state: { origin: 'dashboard' } });
  };

  return (
    <>
      <Typography variant='h1' color='secondary'>
        At a glance
      </Typography>
      <Spacer height='xxs' />
      <Card variant='ghost' color='primary'>
        <CardContent className='pad30'>
          {(selectedOrgMetrics?.pricebookLicenseMetricsList.length ?? 0) === 0 ? (
            <Row style={{ justifyContent: 'center' }}>
              <Typography variant='h3' color='secondary'>
                You do not have any active license agreements.
              </Typography>
            </Row>
          ) : paidBy === PaidByEnum.Org && !meteredPayment ? (
            <Column>
              <Row style={{ alignItems: 'center' }}>
                <Svgs.IconMembersLarge />
                <Spacer width='xs' />
                <Typography variant='p20SemiBold' color='secondary'>
                  Active Users
                </Typography>
                <Spacer width='xs' />
                <Typography variant='p50Bold' color='secondary.dark'>
                  {totalActiveUsers}
                </Typography>
              </Row>
              <Spacer height='xxs' />
              {selectedOrgMetrics &&
                selectedOrgMetrics.pricebookLicenseMetricsList.map((metric, index) => (
                  <Grid key={metric.pricebookId}>
                    <OrgPaysMetricsCard
                      licenseMetric={metric}
                      onPurchaseLicenses={() => onPurchaseLicenses(metric.pricebookId)}
                      purchaseDisabled={typeof pendingAgreement !== 'undefined'}
                      pricebookBundle={pricebookSelections?.find(
                        pb => pb.pricebook.id === metric.pricebookId,
                      )}
                    />
                    {index !== selectedOrgMetrics.pricebookLicenseMetricsList.length - 1 && (
                      <Spacer height='xxs' />
                    )}
                  </Grid>
                ))}
            </Column>
          ) : (
            <Column>
              <Row style={{ justifyContent: 'space-between' }}>
                <Row style={{ alignItems: 'center' }}>
                  <Svgs.IconMembersLarge />
                  <Spacer width='xs' />
                  <Typography variant='p20SemiBold' color='secondary'>
                    Active Users
                  </Typography>
                  <Spacer width='xs' />
                  <Typography variant='p50Bold' color='secondary.dark'>
                    {totalActiveUsers}
                  </Typography>
                </Row>
                {packagesAvailable && groupFeatureMapData?.canAddPackage && (
                  <Button onClick={onAddPackage} color='primary' className='add-package'>
                    Add a package
                  </Button>
                )}
              </Row>
              <Spacer height='xxs' />
              {selectedOrgMetrics &&
                selectedOrgMetrics.pricebookLicenseMetricsList.map((metric, index) => (
                  <Grid key={metric.pricebookId}>
                    <MemberPaysMetricsCard
                      licenseMetric={metric}
                      pricebookBundle={pricebookSelections?.find(
                        pb => pb.pricebook.id === metric.pricebookId,
                      )}
                    />
                    {index !== selectedOrgMetrics.pricebookLicenseMetricsList.length - 1 && (
                      <Spacer height='xxs' />
                    )}
                  </Grid>
                ))}
            </Column>
          )}
        </CardContent>
      </Card>
      {selectedPricebook && (
        <PurchaseLicensesDrawer
          open={purchaseDrawerOpen}
          onClose={() => setPurchaseDrawerOpen(false)}
          pricebook={selectedPricebook}
        />
      )}
    </>
  );
};

export default AtAGlance;
